import React, { useState } from 'react';
import { useStudioStore } from '../../stores/useStudioStore';
import { studioApiService } from '../../services/studioApiService';
import { S3UploadedProduct } from '../../types/studio';

const DBFormCard: React.FC<{ product: S3UploadedProduct }> = ({ product }) => {
  const { updateDbUploadStatus } = useStudioStore();
  
  const [formData, setFormData] = useState({
    name: '',
    color: '',
    category: '',
    available_sizes: '',
    price: '',
    discount_price: '',
    description: '',
    related_products: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate
    const requiredFields = ['name', 'color', 'category', 'available_sizes', 'price', 'discount_price', 'description'];
    const missing = requiredFields.filter(f => !(formData as any)[f]);
    
    if (missing.length > 0) {
      alert(`Missing necessary fields: ${missing.join(', ')}. Please fill them out.`);
      return;
    }

    setIsSubmitting(true);
    
    // Construct payload
    const payload = {
      product_id: product.rowId, // Or a newly generated one
      title: formData.name,
      brand: "Slayd Originals",
      category: formData.category,
      marketplace: "Slayd",
      price: parseFloat(formData.price),
      mrp: parseFloat(formData.discount_price), // assuming mrp is discount_price or vice versa
      product_link: `https://slayd.in/internal/feed/${product.rowId}`,
      image_url: product.imageUrls[0] || '', // _1.png
      is_slayd_originals: true,
      slayd_originals_color: formData.color,
      slayd_originals_size: formData.available_sizes,
      slayd_originals_description: formData.description,
      secondary_images: product.imageUrls.slice(1), // _2, _3, _4
      related_products: formData.related_products ? formData.related_products.split(',').map(s => s.trim()) : []
    };

    try {
      await studioApiService.createProductInDB(payload);
      updateDbUploadStatus(product.rowId, 'uploaded');
      alert("Uploading completed");
    } catch (err: any) {
      alert(`DB Upload failed: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isUploaded = product.status === 'uploaded';

  return (
    <div className="product-card" style={{ borderColor: isUploaded ? 'var(--green)' : 'var(--border)', maxWidth: '600px', margin: '20px auto' }}>
      <div className="product-card-header" style={{ background: isUploaded ? 'var(--green-dim)' : 'transparent' }}>
        <div className="product-card-title">
          Metadata for Product: {product.rowId}
          {isUploaded && <span style={{ color: 'var(--green)', marginLeft: '10px' }}>✔ Completed</span>}
        </div>
      </div>
      
      <div className="product-card-body" style={{ display: 'block' }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <label className="section-label" style={{ marginBottom: '4px' }}>Name *</label>
              <input className="pid-input" name="name" value={formData.name} onChange={handleChange} disabled={isUploaded} />
            </div>
            <div style={{ flex: 1 }}>
              <label className="section-label" style={{ marginBottom: '4px' }}>Category *</label>
              <input className="pid-input" name="category" value={formData.category} onChange={handleChange} disabled={isUploaded} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <label className="section-label" style={{ marginBottom: '4px' }}>Color *</label>
              <input className="pid-input" name="color" value={formData.color} onChange={handleChange} disabled={isUploaded} />
            </div>
            <div style={{ flex: 1 }}>
              <label className="section-label" style={{ marginBottom: '4px' }}>Sizes (CSV) *</label>
              <input className="pid-input" name="available_sizes" value={formData.available_sizes} onChange={handleChange} placeholder="e.g. S, M, L" disabled={isUploaded} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <label className="section-label" style={{ marginBottom: '4px' }}>Price *</label>
              <input type="number" className="pid-input" name="price" value={formData.price} onChange={handleChange} disabled={isUploaded} />
            </div>
            <div style={{ flex: 1 }}>
              <label className="section-label" style={{ marginBottom: '4px' }}>Discount Price *</label>
              <input type="number" className="pid-input" name="discount_price" value={formData.discount_price} onChange={handleChange} disabled={isUploaded} />
            </div>
          </div>

          <div>
            <label className="section-label" style={{ marginBottom: '4px' }}>Description *</label>
            <textarea 
              name="description" 
              value={formData.description} 
              onChange={handleChange}
              disabled={isUploaded}
              style={{ width: '100%', minHeight: '60px' }}
            />
          </div>

          <div>
            <label className="section-label" style={{ marginBottom: '4px' }}>Related Products (Parent ID for Variants)</label>
            <input 
              className="pid-input" 
              name="related_products" 
              value={formData.related_products} 
              onChange={handleChange} 
              placeholder="e.g. 4088" 
              disabled={isUploaded} 
            />
          </div>

          <div style={{ marginTop: '10px' }}>
            <button 
              type="submit" 
              className="run-btn primary" 
              style={{ width: '100%' }}
              disabled={isSubmitting || isUploaded}
            >
              {isUploaded ? 'Completed ✔' : isSubmitting ? 'Submitting...' : 'Upload to DB'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export const DatabaseUpload: React.FC = () => {
  const { dbUploadQueue } = useStudioStore();

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
      {dbUploadQueue.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">✓</div>
          <div className="empty-title">No products ready</div>
          <div className="empty-sub">Push products to S3 from the Generator tabs first.</div>
        </div>
      ) : (
        dbUploadQueue.map(p => (
          <DBFormCard key={p.rowId} product={p} />
        ))
      )}
    </div>
  );
};
